import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  BankOutlined,
  SafetyCertificateOutlined,
  TeamOutlined,
  KeyOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import {
  Avatar,
  Button,
  Card,
  Col,
  Modal,
  Row,
  Space,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Footer from "../../components/Footer";
import NavBar from "../../components/NavBar";
import SideBar from "../../components/SideBar";
import ExportableFilterableTable from "../../components/tables/ExportableFilterableTable";
import { COLORS } from "../../components/ui/colors";
import { searchProfil } from "../../hooks/useSearch";
import { rightsService } from "../../services/rightsService";
import { highlightMatch } from "../../variables/stringFunctions";
import { API_URL, TOKEN } from "../../variables/constants";
import axiosInstance from "../../variables/api/axiosInstance";

const { Title, Text } = Typography;

// Palette deterministe pour les avatars (couleur stable par intitule)
const AVATAR_COLORS = [
  "#1890ff", "#52c41a", "#faad14", "#13c2c2",
  "#722ed1", "#eb2f96", "#fa541c", "#2f54eb",
];
const colorFor = (s = "") =>
  AVATAR_COLORS[[...s].reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_COLORS.length];
const initialsOf = (s = "") =>
  s.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() || "").join("") || "P";

const Profils = () => {
  const [profils, setProfils] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchResults, setSearchResults] = useState([]);
  const [searchValue, setSearchValue] = useState("");

  const navigate = useNavigate();

  const flatten = (content = []) =>
    content.map((p) => {
      const habilitationNames = (p?.habilitations || [])
        .map((h) => h?.libelle || h?.nom || h?.codeHabilitation || h?.code)
        .filter(Boolean);
      return {
        ...p,
        // Un profil sans organisme est un profil "paramètre" (transverse)
        isParametre: !p.organisme,
        organismeName: p.organisme?.raisonSociale,
        // organisme.active est sérialisé par le backend (false = organisme désactivé)
        organismeActive: p.organisme ? p.organisme.active !== false : true,
        habilitationNames,
      };
    });

  // On charge l'ensemble des profils en une fois puis on pagine/filtre côté client.
  const fetchProfils = () => {
    setLoading(true);
    axiosInstance
      .get(`/api/profil`, { params: { page: 0, size: 10000 } })
      .then((res) => {
        setProfils(flatten(res.data?.content));
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchProfils();
  }, []);

  // Profils visibles : on masque ceux rattachés à un organisme INACTIF.
  // Les profils paramètre (sans organisme) restent affichés.
  const visibleProfils = useMemo(
    () => profils.filter((p) => p.isParametre || p.organismeActive),
    [profils]
  );

  const handleDelete = (profil) => {
    Modal.confirm({
      title: "Confirmation de suppression",
      content: `Êtes-vous sûr de vouloir supprimer le profil "${profil.nom}" ?`,
      okText: "Oui",
      okType: "danger",
      cancelText: "Non",
      onOk: () => {
        setLoading(true);
        return axios
          .delete(`${API_URL}/api/profil`, {
            headers: {
              Authorization: `Bearer ${TOKEN}`,
              "Content-Type": "application/json",
            },
            data: profil,
          })
          .then(() => {
            toast("Profil supprimé avec succès !");
            fetchProfils();
          })
          .catch((err) => {
            console.error(err);
            const apiMessage =
              err?.response?.data?.message ||
              err?.response?.data?.error ||
              (typeof err?.response?.data === "string" ? err.response.data : "");
            const lowerMessage = apiMessage?.toLowerCase?.() || "";
            if (lowerMessage.includes("impossible de supprimer ce profil")) {
              toast(apiMessage);
            } else {
              toast("Une erreur est survenue lors de la suppression.");
            }
          })
          .finally(() => setLoading(false));
      },
    });
  };

  const organismFilterOptions = useMemo(
    () =>
      Array.from(new Set(visibleProfils.map((p) => p.organismeName).filter(Boolean))).map(
        (name) => ({ label: name, value: name })
      ),
    [visibleProfils]
  );

  const columns = [
    {
      title: "Intitulé",
      dataIndex: "nom",
      key: "nom",
      searchable: true,
      render: (_, record) => (
        <Space>
          <Avatar style={{ backgroundColor: colorFor(record.nom), fontWeight: 600, flexShrink: 0 }}>
            {initialsOf(record.nom)}
          </Avatar>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, color: COLORS.secondary }}>{record.nom}</div>
            {record.commentaire && (
              <Text type="secondary" style={{ fontSize: 12 }} ellipsis>
                {record.commentaire}
              </Text>
            )}
          </div>
        </Space>
      ),
    },
    {
      title: "Organisme",
      dataIndex: "organismeName",
      key: "organismeName",
      searchable: true,
      // Permet de rechercher "paramètre" et de retrouver les profils transverses
      searchValue: (row) => (row.isParametre ? "Paramètre" : row.organismeName),
      filterType: "select",
      filterOptions: organismFilterOptions,
      exportValue: (row) => (row.isParametre ? "Paramètre" : row.organismeName || ""),
      render: (_, record) =>
        record.isParametre ? (
          <Tag icon={<SettingOutlined />} color="purple" style={{ borderRadius: 6, padding: "2px 10px" }}>
            Paramètre
          </Tag>
        ) : record.organismeName ? (
          <Tag icon={<BankOutlined />} color="blue" style={{ borderRadius: 6, padding: "2px 10px" }}>
            {record.organismeName}
          </Tag>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "Habilitations",
      dataIndex: "habilitationNames",
      key: "habilitations",
      exportValue: (row) => (row.habilitationNames || []).join(" | "),
      render: (names) => {
        const count = names?.length || 0;
        return (
          <Tooltip title={count ? names.slice(0, 12).join(", ") + (count > 12 ? "…" : "") : "Aucune habilitation"}>
            <Tag
              icon={<KeyOutlined />}
              color={count ? "geekblue" : "default"}
              style={{ borderRadius: 6 }}
            >
              {count} habilitation{count > 1 ? "s" : ""}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: "Actions",
      key: "action",
      hideInExport: true,
      width: 120,
      align: "center",
      render: (_, record) => (
        <Space>
          {rightsService("HAC056") && (
            <Tooltip title="Modifier">
              <Button
                type="text"
                shape="circle"
                icon={<EditOutlined style={{ color: COLORS.primary }} />}
                onClick={() => navigate(`/modifier-profil/${record.codeId}`)}
              />
            </Tooltip>
          )}
          {rightsService("HAC058") && (
            <Tooltip title="Supprimer">
              <Button
                type="text"
                shape="circle"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const renderSearchResults = (results) => (
    <div className="px-3 py-2">
      {results.map((item, index) => (
        <div
          key={index}
          onClick={() => navigate(`/modifier-profil/${item.codeId}`)}
          className="dropdown-item d-flex align-items-center gap-2 py-1"
          style={{ cursor: "pointer" }}
        >
          <span className="text-truncate">
            {[item?.nom, item?.organisme?.raisonSociale].map((text, i) => (
              <React.Fragment key={i}>
                {i > 0 && " - "}
                {highlightMatch(text?.toString(), searchValue)}
              </React.Fragment>
            ))}
          </span>
        </div>
      ))}
    </div>
  );

  useEffect(() => {
    if (searchValue && searchValue?.length > 2) {
      searchProfil(searchValue)
        .then((res) => setSearchResults(res.data.content))
        .catch((err) => console.log(err));
    }
  }, [searchValue]);

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <SideBar currentMenu={"PROFILS"} />

        <div className="layout-page">
          <NavBar
            filteredResults={searchResults}
            setFilteredResults={setSearchResults}
            renderSearchResults={renderSearchResults}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
          />

          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">
              {/* En-tete */}
              <Card
                bordered={false}
                style={{
                  borderRadius: 16,
                  marginBottom: 20,
                  background: COLORS.gradient,
                  boxShadow: "0 6px 20px rgba(36,33,102,0.25)",
                }}
                bodyStyle={{ padding: "22px 28px" }}
              >
                <Row align="middle" justify="space-between" gutter={[16, 16]}>
                  <Col>
                    <Space align="center" size="middle">
                      <div
                        style={{
                          width: 56, height: 56, borderRadius: 14,
                          background: "rgba(255,255,255,0.18)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}
                      >
                        <SafetyCertificateOutlined style={{ fontSize: 28, color: "#fff" }} />
                      </div>
                      <div>
                        <Title level={3} style={{ color: "#fff", margin: 0 }}>
                          Gestion des profils
                        </Title>
                        <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                          Profils d'accès et habilitations des utilisateurs
                        </Text>
                      </div>
                    </Space>
                  </Col>
                  {rightsService("HAC057") && (
                    <Col>
                      <Button
                        size="large"
                        icon={<PlusOutlined />}
                        onClick={() => navigate("/ajouter-profil")}
                        style={{
                          background: "#fff",
                          color: COLORS.secondary,
                          fontWeight: 600,
                          border: "none",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                        }}
                      >
                        Créer un profil
                      </Button>
                    </Col>
                  )}
                </Row>
              </Card>

              {/* Tableau */}
              <Card
                bordered={false}
                title={
                  <Space>
                    <TeamOutlined style={{ color: COLORS.primary }} />
                    <span>Liste des profils</span>
                  </Space>
                }
                extra={<Tag color="blue">{visibleProfils.length} profil{visibleProfils.length > 1 ? "s" : ""}</Tag>}
                style={{ borderRadius: 16, boxShadow: "0 4px 16px rgba(0,0,0,0.06)" }}
                bodyStyle={{ paddingTop: 12 }}
              >
                <ExportableFilterableTable
                  columns={columns}
                  dataSource={visibleProfils}
                  exportDataSource={visibleProfils}
                  rowKey="codeId"
                  loading={loading}
                  searchPlaceholder="Rechercher un profil..."
                  exportFileName="profils"
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => `Total : ${total} profil(s)`,
                  }}
                  expandable={{
                    expandedRowRender: (record) => (
                      <div style={{ padding: "4px 8px" }}>
                        <Text type="secondary" style={{ fontWeight: 600 }}>
                          Commentaire :{" "}
                        </Text>
                        <Text>{record.commentaire || "—"}</Text>
                      </div>
                    ),
                  }}
                />
              </Card>

              <Footer />
            </div>
          </div>
        </div>
      </div>

      <div className="layout-overlay layout-menu-toggle"></div>
      <div className="drag-target"></div>
    </div>
  );
};

export default Profils;
